
import React, { useState, useEffect } from "react";
import ShapeTray, { SHAPE_TYPE, CustomDragLayer } from "./ShapeTray";
import { SHAPES } from "./shapes";
import { DndProvider, useDrop } from "react-dnd";
import { MultiBackend } from "react-dnd-multi-backend";
import { HTML5toTouch } from "rdndmb-html5-to-touch";
import { playPlaceSound, playClearSound } from "./sounds";
import "./GameBoard.css";

const BOARD_SIZE = 8;

function emptyBoard() {
  return Array.from({ length: BOARD_SIZE }, () => Array(BOARD_SIZE).fill(0));
}


function getRandomShapes(count = 3) {
  // SHAPES içinden rastgele count kadar şekil seç
  const arr = [];
  const used = new Set();
  while (arr.length < count) {
    const idx = Math.floor(Math.random() * SHAPES.length);
    if (!used.has(idx)) {
      // Deep copy to allow bomb modification
      const shapeCopy = SHAPES[idx].map(row => [...row]);

      // 15% chance to turn one block into a 'Burst Bomb' (value: 2)
      if (Math.random() < 0.15) {
        const filled = [];
        for (let i = 0; i < shapeCopy.length; i++) {
          for (let j = 0; j < shapeCopy[i].length; j++) {
            if (shapeCopy[i][j] === 1) filled.push({ i, j });
          }
        }
        if (filled.length > 0) {
          const randCell = filled[Math.floor(Math.random() * filled.length)];
          shapeCopy[randCell.i][randCell.j] = 2; // 2 = BOMB
        }
      }

      arr.push(shapeCopy);
      used.add(idx);
    }
  }
  return arr;
}

// Yardımcı: Şekil board'a sığar mı?
function canPlaceShape(board, shape, x, y) {
  for (let i = 0; i < shape.length; i++) {
    for (let j = 0; j < shape[i].length; j++) {
      if (shape[i][j]) {
        const bi = x + i;
        const bj = y + j;
        if (
          bi < 0 ||
          bi >= BOARD_SIZE ||
          bj < 0 ||
          bj >= BOARD_SIZE ||
          board[bi][bj] > 0
        ) {
          return false;
        }
      }
    }
  }
  return true;
}

// Yardımcı: Temizlenecek hücreleri ve satır/sütun sayısını bul
function getClearedBoardInfo(board) {
  let clearedCells = [];
  let clearedRows = 0;
  let clearedCols = 0;

  // Satırları kontrol et
  for (let i = 0; i < BOARD_SIZE; i++) {
    if (board[i].every((cell) => cell > 0)) {
      clearedRows++;
      for (let j = 0; j < BOARD_SIZE; j++) {
        clearedCells.push({ i, j });
      }
    }
  }

  // Sütunları kontrol et
  for (let j = 0; j < BOARD_SIZE; j++) {
    let full = true;
    for (let i = 0; i < BOARD_SIZE; i++) {
      if (board[i][j] === 0) {
        full = false;
        break;
      }
    }
    if (full) {
      clearedCols++;
      for (let i = 0; i < BOARD_SIZE; i++) {
        if (!clearedCells.some(c => c.i === i && c.j === j)) {
          clearedCells.push({ i, j });
        }
      }
    }
  }

  // BOMB CHAIN REACTION!
  let bombTriggered = true;
  while (bombTriggered) {
    bombTriggered = false;
    const newBombExplosions = [];

    for (const c of clearedCells) {
      if (board[c.i][c.j] === 2) { // 2 is BOMB
        // Explode 3x3 area
        for (let di = -1; di <= 1; di++) {
          for (let dj = -1; dj <= 1; dj++) {
            const ni = c.i + di;
            const nj = c.j + dj;
            if (ni >= 0 && ni < BOARD_SIZE && nj >= 0 && nj < BOARD_SIZE) {
              if (board[ni][nj] > 0) {
                const alreadyCleared = clearedCells.some(cc => cc.i === ni && cc.j === nj);
                const alreadyAdded = newBombExplosions.some(cc => cc.i === ni && cc.j === nj);
                if (!alreadyCleared && !alreadyAdded) {
                  newBombExplosions.push({ i: ni, j: nj });
                  bombTriggered = true;
                }
              }
            }
          }
        }
      }
    }

    if (newBombExplosions.length > 0) {
      clearedCells.push(...newBombExplosions);
    }
  }

  return { clearedCells, clearedRows, clearedCols };
}

// Yardımcı: Şekli board'a yerleştir (henüz temizlemeden)
function placeShape(board, shape, x, y) {
  const newBoard = board.map((row) => row.slice());
  for (let i = 0; i < shape.length; i++) {
    for (let j = 0; j < shape[i].length; j++) {
      if (shape[i][j] > 0) {
        newBoard[x + i][y + j] = shape[i][j];
      }
    }
  }
  return newBoard;
}

// Yardımcı: Belirli hücreleri board'dan sil
function removeCells(board, cellsToRemove) {
  const newBoard = board.map((row) => row.slice());
  cellsToRemove.forEach(c => {
    newBoard[c.i][c.j] = 0;
  });
  return newBoard;
}

import { useRef, useCallback } from "react";

function BoardDropArea({ board, onDropShape, preview, onHover, onHoverLeave, clearingCells }) {
  const boardRef = useRef(null);

  const getDropCoordinates = (monitor, boardEl, item) => {
    if (!boardEl || !item || !item.shape) return null;
    const clientOffset = monitor.getClientOffset();
    const initialClientOffset = monitor.getInitialClientOffset();
    const initialSourceClientOffset = monitor.getInitialSourceClientOffset();

    if (!clientOffset || !initialClientOffset || !initialSourceClientOffset) return null;

    // Fare ile şeklin orijinal sol üstü arasındaki mesafe (Screen Space)
    const grabOffsetX = initialClientOffset.x - initialSourceClientOffset.x;
    const grabOffsetY = initialClientOffset.y - initialSourceClientOffset.y;

    const boardRect = boardEl.getBoundingClientRect();

    // Hücre boyutları (Grid Cell Size)
    const cellWidth = boardRect.width / BOARD_SIZE;
    const cellHeight = boardRect.height / BOARD_SIZE;

    // Tepsideki hücre boyutu ve Scale oranı (Dynamic Scaling from tray to board)
    const trayCellSize = 26;
    const scaleRatioX = cellWidth / trayCellSize;
    const scaleRatioY = cellHeight / trayCellSize;

    // Şekil büyüdüğü için parmağın altındaki offset de büyümelidir (Pivot & Offset Yönetimi)
    const scaledGrabOffsetX = grabOffsetX * scaleRatioX;
    const scaledGrabOffsetY = grabOffsetY * scaleRatioY;

    // Şeklin ekran üzerindeki nihai (büyütülmüş) sol üst koordinatları
    const shapeX = clientOffset.x - scaledGrabOffsetX;
    const shapeY = clientOffset.y - scaledGrabOffsetY;

    // Şeklin tahtaya göre yerel koordinatları (World Space Dönüşümü)
    const relativeX = shapeX - boardRect.left;
    const relativeY = shapeY - boardRect.top;

    // Snap-to-Grid Algoritması: Matematiksel yuvarlama ile en yakın hücre indeksini bulma
    const targetJ = Math.round(relativeX / cellWidth);
    const targetI = Math.round(relativeY / cellHeight);

    // Cell-Center Proximity Distance Threshold
    const tolerance = (cellWidth + cellHeight) / 2 * 0.75; 
    let allValid = true;

    for (let r = 0; r < item.shape.length; r++) {
      for (let c = 0; c < item.shape[r].length; c++) {
        if (item.shape[r][c] > 0) {
          // Bloğun world center'ı
          const blockWorldCenterX = shapeX + (c + 0.5) * cellWidth;
          const blockWorldCenterY = shapeY + (r + 0.5) * cellHeight;

          // Hedef grid hücresi indeksi
          const gridI = targetI + r;
          const gridJ = targetJ + c;

          if (gridI < 0 || gridI >= BOARD_SIZE || gridJ < 0 || gridJ >= BOARD_SIZE) {
            allValid = false;
            break;
          }

          // Hedef grid hücresinin world center'ı
          const gridCenterX = boardRect.left + (gridJ + 0.5) * cellWidth;
          const gridCenterY = boardRect.top + (gridI + 0.5) * cellHeight;

          const dist = Math.hypot(blockWorldCenterX - gridCenterX, blockWorldCenterY - gridCenterY);
          if (dist > tolerance) {
            allValid = false;
            break;
          }
        }
      }
      if (!allValid) break;
    }

    if (allValid) {
      return { i: targetI, j: targetJ };
    }
    return null;
  };

  const [{ isOver }, drop] = useDrop({
    accept: SHAPE_TYPE,
    drop: (item, monitor) => {
      const coord = getDropCoordinates(monitor, boardRef.current, item);
      if (coord) {
        onDropShape(item, coord.i, coord.j);
      }
    },
    hover: (item, monitor) => {
      const coord = getDropCoordinates(monitor, boardRef.current, item);
      if (coord) {
        onHover(item, coord.i, coord.j);
      } else {
        if (onHoverLeave) onHoverLeave();
      }
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
    }),
  });

  useEffect(() => {
    if (!isOver && onHoverLeave) {
      onHoverLeave();
    }
  }, [isOver, onHoverLeave]);

  // Preview hesabı
  let previewCells = Array.from({ length: BOARD_SIZE }, () => Array(BOARD_SIZE).fill(false));
  if (preview && preview.shape && isOver) {
    const { shape, i: pi, j: pj } = preview;
    for (let si = 0; si < shape.length; si++) {
      for (let sj = 0; sj < shape[si].length; sj++) {
        if (shape[si][sj]) {
          const bi = pi + si;
          const bj = pj + sj;
          if (bi >= 0 && bi < BOARD_SIZE && bj >= 0 && bj < BOARD_SIZE) {
            previewCells[bi][bj] = true;
          }
        }
      }
    }
  }

  const setRefs = useCallback(
    (node) => {
      boardRef.current = node;
      drop(node);
    },
    [drop]
  );

  return (
    <div className="board" ref={setRefs}>
      {board.map((row, i) => (
        <div className="board-row" key={i}>
          {row.map((cell, j) => (
            <CellDrop
              key={j}
              cell={cell}
              i={i}
              j={j}
              isPreviewCell={previewCells[i][j]}
              isClearing={clearingCells.some(c => c.i === i && c.j === j)}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

function CellDrop({ cell, isPreviewCell, isClearing, i, j }) {
  // Animasyon için gecikmeyi i ve j'ye göre hesapla
  // Bu formül ile soldan sağa ve yukarıdan aşağıya tatlı bir dalga efekti oluşacak
  const delay = (i + j) * 0.08;
  const isBomb = cell === 2;

  return (
    <div
      className={`board-cell${cell ? " filled" : ""}${isBomb ? " bomb" : ""}${isPreviewCell ? " preview" : ""}${isClearing ? " clearing" : ""}`}
      style={{
        background: isPreviewCell && !cell ? "rgba(79,195,247,0.4)" : undefined,
        animationDelay: isClearing ? `${delay}s` : undefined
      }}
    >
      {isBomb && <div className="bomb-core"></div>}
    </div>
  );
}

function GameBoard({ onGameOver, score, setScore }) {
  const [board, setBoard] = useState(emptyBoard());
  const [shapes, setShapes] = useState([]);
  const [canGenerate, setCanGenerate] = useState(true);
  const [dragPreview, setDragPreview] = useState(null);
  const [clearingCells, setClearingCells] = useState([]);

  // İlk renderda shapes generate et
  useEffect(() => {
    if (shapes.length === 0 && canGenerate) {
      setShapes(getRandomShapes(3));
      setCanGenerate(false);
    }
  }, [shapes, canGenerate]);

  // Game Over kontrolü
  useEffect(() => {
    if (shapes.length === 0 || clearingCells.length > 0) return;

    let isGameOver = true;
    for (let s = 0; s < shapes.length; s++) {
      const shape = shapes[s];
      for (let i = 0; i < BOARD_SIZE; i++) {
        for (let j = 0; j < BOARD_SIZE; j++) {
          if (canPlaceShape(board, shape, i, j)) {
            isGameOver = false;
            break;
          }
        }
        if (!isGameOver) break;
      }
      if (!isGameOver) break;
    }

    if (isGameOver) {
      // setTimeout to let user see they failed
      const timer = setTimeout(() => onGameOver(score), 1000);
      return () => clearTimeout(timer);
    }
  }, [board, shapes, clearingCells, onGameOver, score]);

  // Şekil drop edildiğinde
  const handleDropShape = (item, i, j) => {
    setDragPreview(null);
    if (canPlaceShape(board, item.shape, i, j)) {
      // Yerleştirme sesi
      playPlaceSound();

      const newBoard = placeShape(board, item.shape, i, j);
      setBoard(newBoard);

      // Temizlenecek alanları kontrol et
      const { clearedCells, clearedRows, clearedCols } = getClearedBoardInfo(newBoard);

      if (clearedCells.length > 0) {
        // Combo Puanı Hesapla: (Satır + Sütun Sayısı) Karesi x 100
        const combo = clearedRows + clearedCols;
        const earnedScore = (combo ** 2) * 100;
        setScore(s => s + earnedScore);

        // Temizleme sesi (combo sayısına göre)
        playClearSound(combo);

        // Animasyonu başlat
        setClearingCells(clearedCells);

        // Dinamik bekleme süresi: En uzak hücrenin gecikmesi (0.08s) + animasyon süresi (0.5s) + buffer (0.1s)
        const maxDelay = Math.max(...clearedCells.map(c => c.i + c.j)) * 0.08;
        const timeout = (maxDelay * 1000) + 600;

        // Animasyon bitince hücreleri gerçekten boşalt
        setTimeout(() => {
          setBoard(prevBoard => removeCells(prevBoard, clearedCells));
          setClearingCells([]);
        }, timeout);
      }

      // shapes listesinden bırakılanı çıkar
      const newShapes = shapes.slice();
      newShapes.splice(item.idx, 1);
      setShapes(newShapes);
      // Eğer tüm şekiller bitti ise yeni generate edilebilir
      if (newShapes.length === 0) setCanGenerate(true);
    }
  };

  // Drag edilen şekil ve hover edilen hücreyi takip et
  const handleHover = (item, i, j) => {
    if (item && canPlaceShape(board, item.shape, i, j)) {
      setDragPreview({ shape: item.shape, i, j });
    } else {
      setDragPreview(null);
    }
  };

  return (
    <DndProvider backend={MultiBackend} options={HTML5toTouch}>
      <CustomDragLayer />
      <div className="game-container">
        <div className="score-container">
          <div className="score-label">SCORE</div>
          <div className="score-value">{score}</div>
        </div>
        <BoardDropArea
          board={board}
          onDropShape={handleDropShape}
          preview={dragPreview}
          onHover={handleHover}
          onHoverLeave={() => setDragPreview(null)}
          clearingCells={clearingCells}
        />
        <ShapeTray shapes={shapes} canGenerate={canGenerate} />
      </div>
    </DndProvider>
  );
}

export default GameBoard;
